import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { SRAlert, SRLevel, SRNotice, SRStatus } from '@contracts/sr';
import { Banner, Empty, Panel } from '../components/Panel';
import { DirectionChip, TrendNote, trendOf } from '../components/SignalPanel';
import { SignalTabs } from '../components/SignalTabs';
import { api } from '../lib/api';
import { dateTime } from '../lib/format';
import { signalPrice } from '../lib/notify';
import { useSR } from '../store/sr';

const KIND_TEXT: Record<SRAlert['kind'], string> = { bounce: 'เด้ง', break: 'ทะลุแล้ว retest' };
const NOTICE_ICON: Record<SRNotice['kind'], string> = {
  daily: '🧱',
  update: '🔄',
  approach: '⚠️',
  touch: '📍',
  wait: '⏳',
  skipped: '⏸',
};
const NOTICE_LABEL: Record<SRNotice['kind'], string> = {
  daily: 'สรุปประจำวัน',
  update: 'อัปเดตแนว',
  approach: 'เข้าใกล้แนว',
  touch: 'แตะแนว',
  wait: 'WAIT',
  skipped: 'ข้ามสัญญาณ',
};

const MAX_NOTICES = 100;
const NOTICES_PER_PAGE = 5;

/** Page buttons: ‹ 1 2 3 … › — the current page is marked with aria-current. */
function Pager({
  page,
  pages,
  onPage,
  label,
}: {
  page: number;
  pages: number;
  onPage: (p: number) => void;
  label: string;
}) {
  if (pages <= 1) return null;
  return (
    <nav className="pager" aria-label={label}>
      <button
        type="button"
        className="btn"
        disabled={page <= 1}
        onClick={() => onPage(page - 1)}
        aria-label="หน้าก่อนหน้า"
      >
        ‹
      </button>
      {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
        <button
          key={p}
          type="button"
          className={`btn${p === page ? ' btn--primary' : ''}`}
          aria-current={p === page ? 'page' : undefined}
          aria-label={`หน้า ${p}`}
          onClick={() => onPage(p)}
        >
          {p}
        </button>
      ))}
      <button
        type="button"
        className="btn"
        disabled={page >= pages}
        onClick={() => onPage(page + 1)}
        aria-label="หน้าถัดไป"
      >
        ›
      </button>
    </nav>
  );
}

function mergeById<T extends { id: string; createdAt: string }>(live: T[], fetched: T[]): T[] {
  const byId = new Map<string, T>();
  for (const x of fetched) byId.set(x.id, x);
  for (const x of live) byId.set(x.id, x); // live updates (outcomes) win
  return [...byId.values()].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

function Stars({ n }: { n: number }) {
  return (
    <span aria-label={`ความแข็งแรง ${n} จาก 3`} title={`${n}/3`}>
      {'★'.repeat(n)}
      <span className="muted">{'☆'.repeat(3 - n)}</span>
    </span>
  );
}

function zone(lower: number, upper: number): string {
  return `${signalPrice(lower)} – ${signalPrice(upper)}`;
}

function LevelRow({ name, lv, sign }: { name: string; lv: SRLevel; sign: string }) {
  return (
    <tr>
      <th scope="row">{name}</th>
      <td className="tabular">{zone(lv.lower, lv.upper)}</td>
      <td>
        <Stars n={lv.stars} />
      </td>
      <td className="r tabular">
        {sign}${lv.distance.toFixed(1)}
      </td>
      <td className="small">{lv.sources.slice(0, 4).join(', ')}</td>
    </tr>
  );
}

function LevelsTable({ status }: { status: SRStatus }) {
  const levels = status.levels;
  if (!levels || (levels.resistance.length === 0 && levels.support.length === 0)) {
    return <Empty title="ยังคำนวณแนวไม่ได้">รอข้อมูลแท่งเทียน H1/M15 จาก MT5</Empty>;
  }
  const inside = levels.inside[0];
  return (
    <div className="table-wrap">
      <table className="ledger">
        <caption className="visually-hidden">แนวต้านด้านบน ราคาปัจจุบัน และแนวรับด้านล่าง</caption>
        <thead>
          <tr>
            <th>แนว</th>
            <th>โซน</th>
            <th>ความแข็งแรง</th>
            <th className="r">ห่างจากราคา</th>
            <th>ที่มา</th>
          </tr>
        </thead>
        <tbody>
          {[...levels.resistance].reverse().map((lv, i, arr) => (
            <LevelRow key={`r${lv.lower}`} name={`R${arr.length - i}`} lv={lv} sign="+" />
          ))}
          <tr className="sr-price-row">
            <th scope="row">ราคา</th>
            <td className="tabular" colSpan={4}>
              <strong>{signalPrice(status.price)}</strong>
              {inside ? <span className="small"> · อยู่ในโซน {zone(inside.lower, inside.upper)}</span> : null}
            </td>
          </tr>
          {levels.support.map((lv, i) => (
            <LevelRow key={`s${lv.lower}`} name={`S${i + 1}`} lv={lv} sign="−" />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OpenAlert({ a }: { a: SRAlert }) {
  return (
    <article className="alert-card" aria-label={`${a.direction} signal, แนวรับแนวต้าน`}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <div className="row">
          <DirectionChip direction={a.direction} />
          <strong>
            {KIND_TEXT[a.kind]} · {a.pattern}
          </strong>
        </div>
        <span className="badge badge--neutral">ไม้เปิดอยู่</span>
      </div>
      <div className="levels levels--4" aria-label="Signal levels">
        <div className="level level--entry">
          <div className="level__label">Entry ({a.direction === 'BUY' ? 'Ask' : 'Bid'})</div>
          <div className="level__value tabular">{signalPrice(a.entry)}</div>
        </div>
        <div className="level level--sl">
          <div className="level__label">Stop loss</div>
          <div className="level__value tabular">{signalPrice(a.stopLoss)}</div>
        </div>
        <div className="level level--tp">
          <div className="level__label">Take profit</div>
          <div className="level__value tabular">{signalPrice(a.takeProfit)}</div>
        </div>
        <div className="level">
          <div className="level__label">At 0.01 lot</div>
          <div className="level__value tabular">
            +${a.reward.toFixed(2)} / −${a.risk.toFixed(2)}
          </div>
        </div>
      </div>
      <TrendNote trend={trendOf(a.details)} />
      <p className="small muted" style={{ margin: 0 }}>
        {dateTime(a.createdAt)}
        {a.details.zone ? ` · โซน ${zone(a.details.zone.lower, a.details.zone.upper)}` : ''}
      </p>
    </article>
  );
}

/** Compact trend for the history table, e.g. " · 📉 ขาลงแรง (สวนเทรนด์)". */
function TrendTag({ details }: { details: SRAlert['details'] }) {
  const t = trendOf(details);
  if (!t) return null;
  return (
    <span className="small muted">
      {' '}
      · {t.label}
      {t.relation === 'counter' ? ' (สวนเทรนด์)' : ''}
    </span>
  );
}

function Result({ a }: { a: SRAlert }) {
  if (a.status === 'tp') return <span className="badge badge--live">✓ ถึง TP</span>;
  if (a.status === 'sl') return <span className="badge badge--danger">✗ ชน SL</span>;
  return <span className="badge badge--neutral">เปิดอยู่</span>;
}

/** Support/resistance alert system: a separate system next to Signals (alerts only, no orders). */
export function SRPage() {
  const live = useSR();
  const statusQ = useQuery({
    queryKey: ['sr-status'],
    queryFn: () => api.get<SRStatus>('/api/v1/sr/status'),
    refetchInterval: 30_000,
  });
  const alertsQ = useQuery({
    queryKey: ['sr-alerts'],
    queryFn: () => api.get<{ alerts: SRAlert[] }>('/api/v1/sr/alerts?limit=200'),
    refetchInterval: 30_000,
  });
  const noticesQ = useQuery({
    queryKey: ['sr-notices'],
    queryFn: () => api.get<{ notices: SRNotice[] }>(`/api/v1/sr/notices?limit=${MAX_NOTICES}`),
    refetchInterval: 30_000,
  });
  const status = live.status ? { ...live.status, channels: statusQ.data?.channels } : (statusQ.data ?? null);
  const alerts = mergeById(live.alerts, alertsQ.data?.alerts ?? []);
  const notices = mergeById(live.notices, noticesQ.data?.notices ?? []).slice(0, MAX_NOTICES);
  const [noticePage, setNoticePage] = useState(1);
  const noticePages = Math.max(1, Math.ceil(notices.length / NOTICES_PER_PAGE));
  const page = Math.min(noticePage, noticePages);
  const pageNotices = notices.slice((page - 1) * NOTICES_PER_PAGE, page * NOTICES_PER_PAGE);
  const closed = alerts.filter((a) => a.status !== 'open');
  const tp = closed.filter((a) => a.status === 'tp').length;
  const pnl = closed.reduce(
    (sum, a) =>
      sum + (a.exitPrice === null ? 0 : a.direction === 'BUY' ? a.exitPrice - a.entry : a.entry - a.exitPrice),
    0,
  );
  const ch = status?.channels;

  return (
    <>
      <header className="page__header">
        <h1>สัญญาณแนวรับแนวต้าน</h1>
        <p className="page__lede">
          ระบบแจ้งเตือนแยกจากสัญญาณ Stochastic: แนวรับแนวต้าน + แท่งเทียนกลับตัวบนแท่ง H1 ที่ปิดแล้ว · TP = 0.5 ×
          ATR(H1) · SL = ปลายไส้แท่งสัญญาณ + เผื่อระยะ (อย่างน้อย $3) · ครั้งละ 1 ไม้ · สรุปแนวประจำวันทุก{' '}
          {status?.config?.dailyHour ?? 8}:00 น.
        </p>
      </header>
      <SignalTabs />

      <Banner tone="info" title="แจ้งเตือนเท่านั้น — ระบบไม่ส่งคำสั่งซื้อขาย">
        ผลทดสอบย้อนหลัง: ได้ผลดีช่วงตลาดผันผวนสูง แต่ขาดทุนช่วงตลาดเงียบ · ไม่ใช่คำแนะนำการลงทุน
      </Banner>

      {status && !status.enabled ? (
        <Banner tone="warn" title="ระบบแนวรับแนวต้านปิดอยู่">
          เปิดได้ด้วย SR_ENABLED=true
        </Banner>
      ) : null}

      <div className="stack" style={{ marginTop: 16 }}>
        <Panel
          title="สถานะตอนนี้"
          meta={
            status?.evaluation?.evaluatedAt ? `H1 ตรวจล่าสุด ${dateTime(status.evaluation.evaluatedAt)}` : undefined
          }
        >
          {statusQ.isError && !status ? (
            <Empty title="โหลดสถานะไม่สำเร็จ">ติดต่อ backend ไม่ได้</Empty>
          ) : !status ? (
            <Empty title="กำลังโหลด…" />
          ) : (
            <>
              <p className="small" style={{ marginTop: 0 }}>
                ราคา <strong className="tabular">{signalPrice(status.price)}</strong> · ATR(H1) ${status.atr.toFixed(1)}
                {status.atrPct !== null ? ` (${status.atrPct.toFixed(2)}% ของราคา)` : ''} · TP ประมาณ $
                {(0.5 * status.atr).toFixed(1)} · ทองวิ่งเฉลี่ยวันละ ~${status.avgRange.toFixed(0)}
                {status.atrPct !== null && status.atrPct < 0.3 ? ' · ⚠️ ตลาดผันผวนต่ำ' : ''}
              </p>
              {status.openAlert ? (
                <OpenAlert a={status.openAlert} />
              ) : (
                <p>
                  <span className="dir dir--none">
                    <span aria-hidden="true">◇</span> WAIT
                  </span>{' '}
                  <span className="small">
                    {status.evaluation?.touches.length
                      ? `แท่ง H1 ล่าสุดแตะแนวแต่ยังไม่ยืนยัน: ${[...new Set(status.evaluation.touches.map((t) => t.reason))].join(' · ')}`
                      : 'ยังไม่มีสัญญาณ — รอราคาเข้าแนวและแท่งยืนยัน'}
                  </span>
                </p>
              )}
              <p className="small muted" style={{ marginBottom: 0 }}>
                ช่องทางแจ้งเตือน: เว็บ ✓ · แอป (Web Push) {ch?.push ? '✓' : 'ปิด'} · LINE {ch?.line ? '✓' : 'ปิด'}
                {ch && !ch.push && !ch.line ? ' — ตอนนี้แสดงบนเว็บอย่างเดียว (เฟสทดลอง)' : ''}
              </p>
            </>
          )}
        </Panel>

        <Panel
          title="แนวรับแนวต้านวันนี้"
          meta={status?.levelsAt ? `คำนวณ ${dateTime(status.levelsAt)} · ฝั่งละไม่เกิน 5 แนว` : undefined}
        >
          {status ? <LevelsTable status={status} /> : <Empty title="กำลังโหลด…" />}
          <p className="small muted">
            ★ = จำนวนวิธีที่ชี้มาที่โซนเดียวกัน (Pivot, High/Low เมื่อวาน, จุดกลับตัว H1/H4/M15, เลขกลม) +
            จำนวนครั้งที่ราคาเคยแตะแล้วเด้ง · แจ้งเตือน “แตะแนว” เฉพาะแนว ★★ ขึ้นไป · แนวคำนวณใหม่ทุกชั่วโมง ถ้ามีแนว ★★
            ขึ้นไปเกิดใหม่ ถูกทะลุ หรือถูกตัดออก จะส่ง “อัปเดตแนว” พร้อมสรุปแนวทั้งหมด
          </p>
        </Panel>

        <Panel
          title="แจ้งเตือนล่าสุด"
          meta={`${notices.length} รายการ${noticePages > 1 ? ` · หน้า ${page}/${noticePages}` : ''}`}
        >
          {notices.length === 0 ? (
            <Empty title="ยังไม่มีแจ้งเตือน">สรุปแนวประจำวันจะมาตอน 8:00 น. ของวันที่ตลาดเปิด</Empty>
          ) : (
            <ul className="sr-notices" aria-label={`แจ้งเตือน หน้า ${page}`}>
              {pageNotices.map((n) => (
                <li key={n.id} className="sr-notice">
                  <div className="small muted">
                    <span aria-hidden="true">{NOTICE_ICON[n.kind]}</span> {NOTICE_LABEL[n.kind]} ·{' '}
                    {dateTime(n.createdAt)}
                    {n.dataSource !== 'live' ? ` · ${n.dataSource.toUpperCase()}` : ''}
                  </div>
                  <div className="small" style={{ whiteSpace: 'pre-line' }}>
                    {n.text}
                  </div>
                </li>
              ))}
            </ul>
          )}
          <Pager page={page} pages={noticePages} onPage={setNoticePage} label="หน้าแจ้งเตือนล่าสุด" />
        </Panel>

        <Panel
          title="ประวัติสัญญาณ"
          meta={`${alerts.length} สัญญาณ · ถึง TP ${tp} · ชน SL ${closed.length - tp}${
            closed.length ? ` (${Math.round((tp / closed.length) * 100)}%)` : ''
          } · รวม ${pnl >= 0 ? '+' : '−'}$${Math.abs(pnl).toFixed(2)} ที่ 0.01 lot`}
        >
          {alerts.length === 0 ? (
            <Empty title={alertsQ.isLoading ? 'กำลังโหลด…' : 'ยังไม่มีสัญญาณ'}>
              WAIT เป็นสถานะปกติ — ระบบจะแจ้ง BUY/SELL เมื่อราคาชนแนวและมีแท่งกลับตัวยืนยัน
            </Empty>
          ) : (
            <div className="table-wrap">
              <table className="ledger">
                <caption className="visually-hidden">สัญญาณแนวรับแนวต้าน ใหม่สุดก่อน</caption>
                <thead>
                  <tr>
                    <th>เวลา</th>
                    <th>สัญญาณ</th>
                    <th>แบบ</th>
                    <th className="r">Entry</th>
                    <th className="r">SL</th>
                    <th className="r">TP</th>
                    <th>ผล</th>
                  </tr>
                </thead>
                <tbody>
                  {alerts.map((a) => (
                    <tr key={a.id}>
                      <td>{dateTime(a.createdAt)}</td>
                      <td>
                        <DirectionChip direction={a.direction} />
                      </td>
                      <td>
                        {KIND_TEXT[a.kind]} · {a.pattern}
                        {a.dataSource !== 'live' ? <span className="small muted"> · {a.dataSource}</span> : null}
                        <TrendTag details={a.details} />
                      </td>
                      <td className="r tabular">{signalPrice(a.entry)}</td>
                      <td className="r tabular">{signalPrice(a.stopLoss)}</td>
                      <td className="r tabular">{signalPrice(a.takeProfit)}</td>
                      <td>
                        <Result a={a} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="disclaimer">
            ผลติดตามจากแท่ง M1 (ฝั่ง SELL ใช้ Bid + spread) ถ้า TP กับ SL เกิดในนาทีเดียวกันนับว่าชน SL ก่อน ·
            กำไร/ขาดทุนก่อนค่าคอมมิชชัน · ไม่ใช่คำแนะนำการลงทุน
          </p>
        </Panel>
      </div>
    </>
  );
}
